import React, {useEffect, useState} from "react";
import {Form, Modal} from "react-bootstrap";
import {useFormik} from "formik";
import {GENERIC_SETTINGS, GENERIC_SETTING_TYPE_CURRENCY} from "../generic/Constants";
import {showToast} from "../../utils/toast/Toast";
import {defaultOtherSetting} from "../generic/InitialStates";
import {apiGenericCreate, apiGenericUpdate, genericGetAll, genericGetOne} from "../generic/crud";
import {AGCSelect} from "../../generic-components/AGCSelect";
import {AGCStaticSelect} from "../../generic-components/AGCStaticSelect";

export function OtherSettingsAddDialog(props) {
    const {
        show,
        handleClose,
        name,
        type,
        settingFieldName,
        settingFieldValues,
        allowAllClasses,
        allowMultiSelect,
        editMode = false,
        settingToEdit = null
    } = props;
    const [objectForSubmit, setObjectForSubmit] = useState(defaultOtherSetting);
    const [isLoading, setIsLoading] = useState(false);
    const [existingSettings, setExistingSettings] = useState([]);
    const [isCurrencyAlreadySet, setIsCurrencyAlreadySet] = useState(false);

    const localHandleClose = (resetForm) => {
        handleClose();
        resetForm();
    };

    const formik = useFormik({
        initialValues: objectForSubmit,
        enableReinitialize: true,
        onSubmit(obj, {resetForm}) {
            setIsLoading(true);

            let objToSubmit;
            
            if (editMode && obj.id) {
                // For update, keep all existing properties and just update the settingValue
                objToSubmit = { ...obj };
                objToSubmit.settingValue = obj.settingValue.id;

            } else {
                // For new creation, just use minimum required fields
                objToSubmit = {
                    settingType: type,
                    settingValue: obj.settingValue.id
                };
            }

            console.log("Submitting object:", objToSubmit);

            if (editMode && obj.id) {
                // Update existing setting
                apiGenericUpdate(GENERIC_SETTINGS, objToSubmit, obj.id)
                    .then(() => {
                        setIsLoading(false);
                        localHandleClose(resetForm);
                        showToast(name + " is Updated!");
                    })
                    .catch((error) => {
                        console.error("Error updating setting:", error);
                        setIsLoading(false);
                    });
            } else {
                // Create new setting
                apiGenericCreate(GENERIC_SETTINGS, objToSubmit)
                    .then(() => {
                        setIsLoading(false);
                        localHandleClose(resetForm);
                        showToast(name + " is Created!");
                    })
                    .catch((error) => {
                        console.error("Error creating setting:", error);
                        setIsLoading(false);
                    });
            }
        },
    });

    // Check if currency is already set
    useEffect(() => {
        if (show && type === GENERIC_SETTING_TYPE_CURRENCY) {
            setIsLoading(true);
            genericGetAll(GENERIC_SETTINGS, {})
                .then(({data}) => {
                    const settings = data.entities || data.payload || [];
                    setExistingSettings(settings);
                    
                    // Check if currency setting already exists
                    const currencySettings = settings.filter(setting => setting.settingType === GENERIC_SETTING_TYPE_CURRENCY);
                    setIsCurrencyAlreadySet(currencySettings.length > 0);
                    
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching settings:", error);
                    setIsLoading(false);
                });
        }
    }, [show, type]);

    useEffect(() => {
        if (editMode && settingToEdit) {
            // For edit mode, we need to get the full object to preserve orgId
            setIsLoading(true);
            
            genericGetOne(GENERIC_SETTINGS, settingToEdit.id)
                .then(({data}) => {
                    // Convert the setting value ID back to an object for the select component
                    const foundValue = settingFieldValues.find(v => v.id === data.settingValue);
                    const settingValueObj = foundValue ? foundValue : settingFieldValues[0];
                    
                    setObjectForSubmit({
                        ...data,
                        settingValue: settingValueObj
                    });
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching setting details:", error);
                    // Fallback to the provided settingToEdit if fetch fails
                    const foundValue = settingFieldValues.find(v => v.id === settingToEdit.settingValue);
                    const settingValueObj = foundValue ? foundValue : settingFieldValues[0];
                    
                    setObjectForSubmit({
                        ...settingToEdit,
                        settingValue: settingValueObj
                    });
                    setIsLoading(false);
                });
        } else {
            setObjectForSubmit(defaultOtherSetting);
        }
    }, [show, editMode, settingToEdit, settingFieldValues]);

    return (
        <Modal
            size="sm"
            show={show}
            onHide={() => localHandleClose(formik.resetForm)} // Pass resetForm to localHandleClose
            aria-labelledby="example-modal-sizes-title-lg"
        >
            <Modal.Header closeButton>
                <Modal.Title>{editMode ? `Edit ${name}` : name}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="overlay">
                {!isLoading && (
                    <Form className="form form-label-right">
                        {type === GENERIC_SETTING_TYPE_CURRENCY && isCurrencyAlreadySet && !editMode ? (
                            <div className="alert alert-warning">
                                Currency is already set in the system. Only one currency can be used at a time.
                                Please edit the existing currency setting instead.
                            </div>
                        ) : (
                            <>
                                <div className="form-group row">
                                    <Form.Group className="col-lg-12">
                                        <AGCStaticSelect
                                            data={settingFieldValues}
                                            formik={formik}
                                            isMulti={allowMultiSelect}
                                            id={"settingValue"}
                                            name={settingFieldName}
                                            value={formik.values.settingValue}
                                        />
                                    </Form.Group>
                                </div>
                            </>
                        )}

                        <br/>
                        <Modal.Footer>
                            <div>
                                <button
                                    type="reset"
                                    className="btn btn-light btn-elevate me-4"
                                    onClick={() => localHandleClose(formik.resetForm)} // Pass resetForm to localHandleClose
                                >
                                    Cancel
                                </button>
                                {!(type === GENERIC_SETTING_TYPE_CURRENCY && isCurrencyAlreadySet && !editMode) && (
                                    <button
                                        type="submit"
                                        onClick={formik.handleSubmit}
                                        className="btn btn-primary btn-elevate"
                                    >
                                        Save
                                    </button>
                                )}
                            </div>
                        </Modal.Footer>
                    </Form>
                )}
                {isLoading && <span>Please Wait, Fetching Information from Server</span>}
            </Modal.Body>
        </Modal>
    );
}